import { type ReactNode } from "react";
import { Provider as ReduxProvider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "../components/ui/sonner";
import { store, persistor } from "../stores/store";
import { queryClient } from "../lib/queryClient";
import { SocketProvider } from "@/sockets/socketContext";
import { useGlobalSocketEvents } from "@/sockets/hooks/useGlobalSocketEvents";
import { BulkPillHost } from "@/features/leads/components/bulk-update/BulkOperationPill";
import ToneAlertHost from "@/features/notifications/tone/ToneAlertHost";
import TransitionNoteHost from "@/features/leads/components/transition-note/TransitionNoteHost";
import TransitionFollowUpHost from "@/features/leads/components/transition-note/TransitionFollowUpHost";
import TrackRequirementHost from "@/features/leads/components/transition-note/TrackRequirementHost";
import ClientIntegrityHost from "@/features/security/ClientIntegrityHost";

interface AppProvidersProps {
  children: ReactNode;
}

export const AppProviders = ({ children }: AppProvidersProps) => {
  useGlobalSocketEvents();

  return (
    <ReduxProvider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <QueryClientProvider client={queryClient}>
          <SocketProvider>
            {children}
            <Toaster closeButton />
            {/* Bulk-progress pills — side-by-side top-center row, outlives route changes. */}
            <BulkPillHost />
            {/* Stage/status notification-tone popup — rings once per coalesced batch. */}
            <ToneAlertHost />
            {/* "Note required" dialog for stage moves gated by a Notes requirement. */}
            <TransitionNoteHost />
            <TransitionFollowUpHost />
            {/* "Needs a few things first" dialog for track status changes gated by a track requirement. */}
            <TrackRequirementHost />
            {/* Report-only probe for patched fetch/XHR or webdriver automation. */}
            <ClientIntegrityHost />
          </SocketProvider>
        </QueryClientProvider>
      </PersistGate>
    </ReduxProvider>
  );
};
